import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const COLORS = ['#1976d2', '#4caf50', '#ff9800', '#9c27b0', '#e91e63'];

// 生成模拟数据
const generateBrokerData = () => {
  return {
    id: 'broker-001',
    name: 'John Smith',
    branch: 'Downtown Branch',
    metrics: {
      monthlyPolicies: 25,
      annualSubmit: 300,
      monthlyPrem: 75000,
      autoRatio: 60,
      homeRatio: 30,
      commercialRatio: 10
    },
    policyHistory: Array(12).fill(null).map((_, index) => ({
      month: new Date(2024, index).toLocaleString('en-US', { month: 'short' }),
      policies: 20 + Math.floor(Math.random() * 10),
      premium: 65000 + Math.floor(Math.random() * 20000)
    })),
    policyTypes: [
      { name: 'Auto', value: 60 },
      { name: 'Home', value: 30 },
      { name: 'Commercial', value: 10 }
    ],
    ageDistribution: [
      { name: '18-25', value: 15 },
      { name: '26-35', value: 35 },
      { name: '36-50', value: 30 },
      { name: '51+', value: 20 }
    ]
  };
};

const BrokerPerformanceDashboard = () => {
  const [timeRange, setTimeRange] = useState('monthly');
  const brokerData = generateBrokerData();

  // 关键指标卡片数据 - 添加年保费
  const keyMetrics = [
    { 
      label: 'Monthly Policies',
      value: brokerData.metrics.monthlyPolicies,
      icon: <AssessmentIcon sx={{ color: '#1976d2' }} />
    },
    { 
      label: 'Annual Submissions',
      value: brokerData.metrics.annualSubmit,
      icon: <TrendingUpIcon sx={{ color: '#4caf50' }} />
    },
    { 
      label: 'Monthly Premium',
      value: `$${brokerData.metrics.monthlyPrem.toLocaleString()}`,
      icon: <GroupIcon sx={{ color: '#ff9800' }} />
    },
    { 
      label: 'Annual Premium',
      value: `$${(brokerData.metrics.monthlyPrem * 12).toLocaleString()}`,
      icon: <GroupIcon sx={{ color: '#9c27b0' }} />
    }
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        {/* 标题和基本信息 */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ 
            color: '#1976d2', 
            mb: 1,
            fontWeight: 600 
          }}>
            Broker Performance Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {brokerData.name} - {brokerData.branch}
          </Typography>
        </Box>

        {/* 时间范围选择器 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="annual">Annual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 关键指标卡片 - 调整为4列 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {keyMetrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {metric.icon}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
                      {metric.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5">
                    {metric.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 分布图表区域 - 并排显示 */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* 保单类型分布 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Policy Type Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={brokerData.policyTypes}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  >
                    {brokerData.policyTypes.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="middle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* 年龄分布 */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, height: 400 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                Age Distribution
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie
                    data={brokerData.ageDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                  >
                    {brokerData.ageDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="middle"
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* 月度趋势图 - 移到最下面 */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Monthly Performance Trends
          </Typography>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={brokerData.policyHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="policies" fill="#1976d2" name="Policies" />
                <Bar yAxisId="right" dataKey="premium" fill="#4caf50" name="Premium" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default BrokerPerformanceDashboard; 